import { call, put, takeLatest } from "redux-saga/effects";
import { replace } from "connected-react-router";
import { types, STATUS, actions as environmentActions } from "reducers/environment";
import { types as onboardingTypes, selectors as onboardingSelectors } from "reducers/onboarding";
import { store } from "store";
import * as environment from "middleware/environment";
import { adjustIdFieldErrors } from "util/form.js";
import gaEventTracker, { events } from "util/gaEventTracker";
import { actions as notificationActions } from "reducers/notification";
import * as i18n from "util/i18n";

const sagas = [
    takeLatest([types.CURRENT_STEP_REQUEST], getCurrentStep),

    takeLatest([types.GENERAL_DATA_SELECTORS_REQUEST], getGeneralDataSelectors),
    takeLatest([types.SAVE_GENERAL_DATA_REQUEST], saveGeneralData),

    takeLatest([types.COMPLEMENTARY_DATA_SELECTORS_REQUEST], getComplementaryDataSelectors),
    takeLatest([types.UIF_DETAILS_REQUEST], verifyUifDetails),
    // takeLatest([types.SAVE_COMPLEMENTARY_DATA_REQUEST], saveComplementaryData),

    takeLatest([types.SAVE_LEGAL_REPRESENTATIVE_REQUEST], saveLegalRepresentative),

    takeLatest([types.SAVE_SHAREHOLDERS_REQUEST], saveShareholders),

    takeLatest([types.UPLOAD_DOCUMENT_REQUEST], uploadDocument),

    takeLatest([types.SAVE_DOCUMENTATION_REQUEST], saveDocumentation),

    takeLatest([types.VERIFY_PERSON_REQUEST], verifyPerson),

    takeLatest([types.BENEFICIARIES_SELECTORS_REQUEST], getBeneficiariesSelectors),

    takeLatest([types.REQUEST_LIST_STATES], listStates),

    takeLatest([types.REQUEST_LIST_SHAREHOLDERS_BENEFICIARIES], listShareholdersBeneficiaries),

    takeLatest([types.SAVE_BENEFICIARIES_REQUEST], saveBeneficiaries),

    takeLatest([types.SAVE_PROXIES_REQUEST], saveProxy),

    takeLatest(types.VERIFY_TAX_SELECTOR_REQUEST, getTaxSelectorRequest),

    takeLatest([types.SAVE_TAX_REQUEST], saveTax),

    takeLatest([types.GET_STEP_DATA_REQUEST], getStepData),

    takeLatest([types.DELETE_SHAREHOLDERS_REQUEST], deleteShareholder),

    takeLatest([types.VERIFY_PERSON_ROLES_REQUEST], verifyPersonRoles),

    takeLatest([types.ACCEPT_PAYWAY_REQUEST], saveAcceptPayway),

    takeLatest([types.SELECT_PAYWAY_TERMINAL_TYPE_REQUEST], savePaywayTerminalType),

    takeLatest([types.GET_IS_PAYWAY_ACCEPTED_REQUEST], getIsPaywayAccepted),

    takeLatest([types.GET_PAYWAY_CATEGORIES_REQUEST], getPaywayCategories),

    takeLatest([types.VERIFY_ACCEPTED_TYC_REQUEST], verifyTyCAcceptance),

    takeLatest([types.SAVE_PAYWAY_CATEGORY_REQUEST], savePaywayCategory),

    takeLatest([types.SAVE_PAYWAY_TERMINALS_DATA_REQUEST], savePaywayTerminalsData),

    takeLatest([types.PAYWAY_LAST_STEP_REQUEST], lastStepPaywayPJ),
];

export default sagas;

function* getCurrentStep() {
    try {
        const response = yield call(environment.getCurrentStep);
        // const response = yield call(onboarding.listPersonalInfo);
        if (response?.data?.code === "COR000I") {
            if (response?.data?.data?.onboarding != null) {
                // response.data.data.onboarding.status="IN_REVIEW"
                const { documentNumber, documentType } = response?.data?.data?.onboarding;

                if (documentNumber) {
                    yield put({
                        type: types.CURRENT_STEP_SUCCESS,
                        documentNumber,
                        documentCountry: null,
                        firstName: null,
                        fullDocument: null,
                        lastName: null,
                        dateOfBirth: null,
                    });
                }
                if (documentType) {
                    yield put({
                        type: onboardingTypes.SET_DOCUMENT_TYPE,
                        documentType,
                    });
                }
            }
            yield put({
                type: types.CURRENT_STEP_SUCCESS,
                currentStepRequest: response?.data?.data,
            });
        } else {
            yield put({
                type: types.CURRENT_STEP_FAILURE,
                currentStepRequest: response?.data?.message || response?.data?.data?.message,
            });
        }
    } catch (error) {
        yield put({
            type: types.CURRENT_STEP_FAILURE,
            currentStepRequest: error?.response?.data?.message || error?.data?.message,
        });
        // yield put(notificationActions.showNotification(response.data.message, "error", ["RegisterStep3Part1"]));
    }
}

function* getGeneralDataSelectors({ guestFlow }) {
    try {
        const response = yield call(environment.getGeneralSelectors, { guestFlow });

        if (response?.data?.code === "COR000I") {
            const {
                countrySelector,
                provinceSelector,
                businessName,
                documentNumber,
                fiscalAddress,
            } = response?.data?.data;

            yield put({
                type: types.GENERAL_DATA_SELECTORS_SUCCESS,
                generalDataSelectorsRequest: {
                    countrySelector,
                    provinceSelector,
                    businessName,
                    documentNumber,
                    fiscalAddress,
                },
            });
        } else {
            yield put({
                type: types.GENERAL_DATA_SELECTORS_FAILURE,
                generalDataSelectorsRequest: response?.data?.message || response?.data?.data?.message,
            });
        }
    } catch (error) {
        yield put({
            type: types.GENERAL_DATA_SELECTORS_FAILURE,
            generalDataSelectorsRequest: error?.response?.data?.message || error?.data?.message,
        });
        // yield put(notificationActions.showNotification(response.data.message, "error", ["RegisterStep3Part1"]));
    }
}

function* saveGeneralData({ generalData, UIFDetails }) {
    try {
        const response = yield call(environment.saveGeneralData, { generalData, UIFDetails });

        if (response && response.data?.code === "COR000I") {
            const {
                legalCountry,
                taxCountry,
                legalProvince,
                taxProvince,
                fatca,
                economicGroup,
                numOfCompanies,
            } = generalData;
            gaEventTracker.triggerEvent(
                events.registry13(
                    legalCountry || taxCountry,
                    legalProvince || taxProvince,
                    fatca ? "si" : "no",
                    economicGroup ? "si" : "no",
                    numOfCompanies,
                    !UIFDetails.lenght ? "no" : "si",
                ),
            );
            yield put({ type: types.SAVE_GENERAL_DATA_SUCCESS, generalDataRequest: response.data?.data });
        } else {
            yield put({
                type: types.SAVE_GENERAL_DATA_FAILURE,
                generalDataRequest: response?.data?.message || response?.data?.data?.message,
            });
        }
    } catch (error) {
        yield put({
            type: types.SAVE_GENERAL_DATA_FAILURE,
            generalDataRequest: error?.response?.data?.message || error?.data?.message,
        });
        // yield put(notificationActions.showNotification(response.data.message, "error", ["RegisterStep3Part1"]));
    }
}

function* getComplementaryDataSelectors({ documentNumber = "" }) {
    try {
        const response = yield call(environment.getComplementaryDataSelectors, { documentNumber });

        if (response?.data?.code !== "COR000I") {
            yield put({
                type: types.COMPLEMENTARY_DATA_SELECTORS_FAILURE,
                complementaryDataSelectorsRequest: response?.data?.message || response?.data?.data?.message,
            });
        } else {
            const {
                categoriesFATCASelector,
                categoriesCRSOCDESelector,
                countrySelector,
                legalAddress,
                legalPersonCDA,
            } = response?.data?.data;

            yield put({
                type: types.COMPLEMENTARY_DATA_SELECTORS_SUCCESS,
                complementaryDataSelectorsRequest: {
                    categoriesFATCASelector,
                    categoriesCRSOCDESelector,
                    countrySelector,
                    legalAddress,
                    legalPersonCDA,
                },
            });
        }
    } catch (error) {
        yield put({
            type: types.COMPLEMENTARY_DATA_SELECTORS_FAILURE,
            complementaryDataSelectorsRequest: error?.response?.data?.message || error?.data?.message,
        });
        // yield put(notificationActions.showNotification(response.data.message, "error", ["RegisterStep3Part1"]));
    }
}

function* verifyUifDetails({ cuit }) {
    try {
        const response = yield call(environment.verifyUifDetails, { cuit });

        if (response && response.data?.code === "COR000I") {
            yield put({ type: types.UIF_DETAILS_SUCCESS, uifDetailsRequest: response.data?.data?.UIFDetails });
        } else {
            let errorMessage = response?.data?.message || response?.data?.data?.message;
            if (response?.data?.data.cuit) {
                errorMessage = i18n.get(response?.data?.data.cuit, "Error al consultar información financiera");
            }
            yield put({
                type: types.UIF_DETAILS_FAILURE,
                uifDetailsRequest: errorMessage,
            });
            yield put(notificationActions.showNotification(errorMessage, "error", ["environment"]));
        }
    } catch (error) {
        yield put({
            type: types.UIF_DETAILS_FAILURE,
            uifDetailsRequest: error?.response?.data?.message || error?.data?.message,
        });
        // yield put(notificationActions.showNotification(response.data.message, "error", ["RegisterStep3Part1"]));
    }
}

// function* saveComplementaryData({ complementaryData, UIFDetails }) {
//     try {
//         const params = { complementaryData, UIFDetails };
//         const response = yield call(environment.saveComplementaryData, params);

//         if (response && response.data?.code === "COR000I") {
//             const { fatca, economicGroup, numOfCompanies } = complementaryData;
//             gaEventTracker.triggerEvent(
//                 events.registry14(
//                     fatca ? "si" : "no",
//                     economicGroup ? "si" : "no",
//                     numOfCompanies,
//                     !UIFDetails.lenght ? "no" : "si",
//                 ),
//             );

//             yield put({ type: types.SAVE_COMPLEMENTARY_DATA_SUCCESS, complementaryDataRequest: response.data?.data });
//         } else {
//             yield put({
//                 type: types.SAVE_COMPLEMENTARY_DATA_FAILURE,
//                 complementaryDataRequest: response?.data?.message || response?.data?.data?.message,
//             });
//         }
//     } catch (error) {
//         yield put({
//             type: types.SAVE_COMPLEMENTARY_DATA_FAILURE,
//             complementaryDataRequest: error?.response?.data?.message || error?.data?.message,
//         });
//         // yield put(notificationActions.showNotification(response.data.message, "error", ["RegisterStep3Part1"]));
//     }
// }

function* saveLegalRepresentative({ legalRepresentative }) {
    try {
        const params = { legalRepresentativeData: legalRepresentative };
        const response = yield call(environment.saveLegalRepresentative, params);

        if (response && response.data?.code === "COR000I") {
            const { signer, pep, residentCountry, fatca } = legalRepresentative;
            const countryList = onboardingSelectors.getCountryList(store.getState());
            let countryName = "";
            if (residentCountry !== "") {
                const country = countryList.find((c) => c.value === residentCountry);
                countryName = country.label;
            }
            gaEventTracker.triggerEvent(
                events.registry16(signer ? "si" : "no", pep ? "si" : "no", countryName, fatca ? "si" : "no"),
            );
            yield put({
                type: types.SAVE_LEGAL_REPRESENTATIVE_SUCCESS,
                legalRepresentativeRequest: response.data?.data,
            });
        } else {
            const errorMessage = Object.values(response?.data?.data || [])[0] || "";

            yield put({
                type: types.SAVE_LEGAL_REPRESENTATIVE_FAILURE,
                legalRepresentativeRequest: response?.data?.message || response?.data?.data?.message,
                errorMessage,
            });
            yield put(notificationActions.showNotification(errorMessage, "error", ["environment"]));
        }
    } catch (error) {
        yield put({
            type: types.SAVE_LEGAL_REPRESENTATIVE_FAILURE,
            legalRepresentativeRequest: error?.response?.data?.message || error?.data?.message,
        });
        // yield put(notificationActions.showNotification(error?.response?.data?.message, "error", ["StepError"]));
    }
}

function* saveShareholders({ shareholders }) {
    try {
        const params = { shareholdersData: shareholders };
        const response = yield call(environment.saveShareholders, params);

        if (response && response.data?.code === "COR000I") {
            const { signer } = shareholders;
            gaEventTracker.triggerEvent(events.registry17(signer));
            yield put({
                type: types.SAVE_SHAREHOLDERS_SUCCESS,
                shareholdersRequest: response.data?.data,
            });
        } else {
            yield put({
                type: types.SAVE_SHAREHOLDERS_FAILURE,
                shareholdersRequest: response?.data?.message || response?.data?.data?.message,
            });
        }
    } catch (error) {
        yield put({
            type: types.SAVE_SHAREHOLDERS_FAILURE,
            shareholdersRequest: error?.response?.data?.message || error?.data?.message,
        });
        // yield put(notificationActions.showNotification(response.data.message, "error", ["RegisterStep3Part1"]));
    }
}

function* uploadDocument({ documentToUpload, isOnboarding = true }) {
    try {
        const params = {
            documentNumber: documentToUpload.documentNumber,
            documentType: documentToUpload.documentType,
            documentCode: documentToUpload.documentCode,
            documentName: documentToUpload.documentName,
            _documentToSave: documentToUpload._documentToSave,
            isOnboarding,
        };
        const response = yield call(environment.uploadDocument, params);

        if (response && response.data?.code === "COR000I") {
            yield put({
                type: types.UPLOAD_DOCUMENT_SUCCESS,
                documentToUploadRequest: response.data?.data,
                documentCode: documentToUpload.uploaderRequestId,
            });
        } else {
            yield put({
                type: types.UPLOAD_DOCUMENT_FAILURE,
                documentToUploadRequest: response?.data?.message || response?.data?.data?.message,
                documentCode: documentToUpload.uploaderRequestId,
            });
        }
    } catch (error) {
        yield put({
            type: types.UPLOAD_DOCUMENT_FAILURE,
            documentToUploadRequest: error?.response?.data?.message || error?.data?.message,
            documentCode: documentToUpload.uploaderRequestId,
        });
        // yield put(notificationActions.showNotification(response.data.message, "error", ["RegisterStep3Part1"]));
    }
}

function* saveDocumentation({ documentation }) {
    try {
        const params = { documentData: documentation };
        const response = yield call(environment.saveDocumentation, params);

        if (response && response.data?.code === "COR000I") {
            gaEventTracker.triggerEvent(events.registry20);
            yield put({
                type: types.SAVE_DOCUMENTATION_SUCCESS,
                documentationRequest: response.data?.data,
            });
        } else {
            yield put({
                type: types.SAVE_DOCUMENTATION_FAILURE,
                documentationRequest: response?.data?.message || response?.data?.data?.message,
            });
        }
    } catch (error) {
        yield put({
            type: types.SAVE_DOCUMENTATION_FAILURE,
            documentationRequest: error?.response?.data?.message || error?.data?.message,
        });
    }
}

function* verifyPerson(documentData) {
    try {
        const response = yield call(environment.verifyPerson, documentData);

        if (response && response.data?.code === "COR000I") {
            yield put({
                type: types.VERIFY_PERSON_SUCCESS,
                verifyPersonRequest: response.data?.data,
                documentNumber: documentData.documentNumber,
            });
        } else {
            yield put({
                type: types.VERIFY_PERSON_FAILURE,
                verifyPersonRequest: response?.data?.message || response?.data?.data?.message,
                documentNumber: documentData.documentNumber,
            });
        }
    } catch (error) {
        yield put({
            type: types.VERIFY_PERSON_FAILURE,
            verifyPersonRequest: error?.response?.data?.message || error?.data?.message,
            documentNumber: documentData.documentNumber,
        });
        // yield put(notificationActions.showNotification(response.data.message, "error", ["RegisterStep3Part1"]));
    }
}

function* getBeneficiariesSelectors() {
    try {
        const response = yield call(environment.getBeneficiariesSelectors);

        if (response?.data?.code !== "COR000I") {
            yield put({
                type: types.BENEFICIARIES_SELECTORS_FAILURE,
            });
        } else {
            const {
                categoriesFATCASelector,
                categoriesCRSOCDESelector,
                countrySelector,
                pepJobsSelector,
                pepRelationshipTypesSelector,
            } = response?.data?.data;

            yield put({
                type: types.BENEFICIARIES_SELECTORS_SUCCESS,
                beneficiariesSelectors: {
                    status: STATUS.SUCCESS,
                    categoriesFATCASelector,
                    categoriesCRSOCDESelector,
                    countrySelector,
                    pepJobsSelector: pepJobsSelector.reduce(
                        (newList, { stringId, shortDesc }) => [...newList, { value: stringId, label: shortDesc }],
                        [],
                    ),
                    pepRelationshipTypesSelector: pepRelationshipTypesSelector.reduce(
                        (newList, { stringId, shortDesc }) => [...newList, { value: stringId, label: shortDesc }],
                        [],
                    ),
                },
            });
        }
    } catch (error) {
        yield put({
            type: types.BENEFICIARIES_SELECTORS_FAILURE,
        });
        // yield put(notificationActions.showNotification(response.data.message, "error", ["RegisterStep3Part1"]));
    }
}

function* listStates({ countryCode }) {
    const response = yield call(environment.listStates, { countryCode });

    if (response) {
        if (response.type === "W") {
            const errors = adjustIdFieldErrors(response.data.data);

            yield put({
                type: types.FAILURE_LIST_STATES,
                errors,
            });
        } else {
            const { states } = response.data.data;

            yield put({
                type: types.SUCCESS_LIST_STATES,
                states,
            });
        }
    }
}

function* listShareholdersBeneficiaries({ setAlreadyFetchShareholders }) {
    try {
        const response = yield call(environment.listShareholdersBeneficiaries);

        if (response) {
            if (response.type === "W") {
                const errors = adjustIdFieldErrors(response.data.data);

                yield put({
                    type: types.FAILURE_LIST_SHAREHOLDERS_BENEFICIARIES,
                    errors,
                });
            } else {
                const { beneficiaries } = response.data.data;

                yield put({
                    type: types.SUCCESS_LIST_SHAREHOLDERS_BENEFICIARIES,
                    shareholdersBeneficiaries: beneficiaries,
                });
            }
        }
    } finally {
        setAlreadyFetchShareholders(true);
    }
}

function* saveBeneficiaries({ beneficiariesRequest }) {
    try {
        const response = yield call(environment.saveFinalBeneficiaries, beneficiariesRequest);

        if (response && response.data?.code === "COR000I") {
            gaEventTracker.triggerEvent(events.registry19);
            yield put({
                type: types.SAVE_BENEFICIARIES_SUCCESS,
                beneficiariesRequest: response.data?.data,
            });
        } else {
            yield put({
                type: types.SAVE_BENEFICIARIES_FAILURE,
                beneficiariesRequest: response?.data?.message || response?.data?.data?.message,
            });
        }
    } catch (error) {
        yield put({
            type: types.SAVE_BENEFICIARIES_FAILURE,
            beneficiariesRequest: error?.response?.data?.message || error?.data?.message,
        });
    }
}

function* saveProxy({ proxy }) {
    try {
        const params = { proxiesData: proxy };
        const response = yield call(environment.saveProxy, params);

        if (response && response.data?.code === "COR000I") {
            gaEventTracker.triggerEvent(events.registry18);
            yield put({
                type: types.SAVE_PROXIES_SUCCESS,
                proxyRequest: response.data?.data,
            });
        } else {
            yield put({
                type: types.SAVE_PROXIES_FAILURE,
                proxyRequest: response?.data?.message || response?.data?.data?.message,
            });
        }
    } catch (error) {
        yield put({
            type: types.SAVE_PROXIES_FAILURE,
            proxyRequest: error?.response?.data?.message || error?.data?.message,
        });
    }
}

function* getTaxSelectorRequest() {
    try {
        const response = yield call(environment.getTaxSelectorRequest);

        if (response && response.data?.code === "COR000I") {
            yield put({
                type: types.VERIFY_TAX_SELECTOR_SUCCESS,
                taxSelectorRequest: response.data?.data,
            });
        } else {
            yield put({
                type: types.VERIFY_TAX_SELECTOR_FAILURE,
                taxSelectorRequest: response?.data?.message || response?.data?.data?.message,
            });
        }
    } catch (error) {
        yield put({
            type: types.VERIFY_TAX_SELECTOR_FAILURE,
            taxSelectorRequest: error?.response?.data?.message || error?.data?.message,
        });
    }
}

function* saveTax({ tax }) {
    try {
        const params = { taxData: tax };
        const response = yield call(environment.saveTax, params);

        if (response && response.data?.code === "COR000I") {
            const { certificateMiPyme, registeredResponsible, taxIIBB, taxStamp } = tax;
            gaEventTracker.triggerEvent(
                events.registry15(
                    certificateMiPyme?.myPyme ? "si" : "no",
                    registeredResponsible ? "si" : "no",
                    registeredResponsible ? "si" : "no",
                    taxIIBB ? "si" : "no",
                    taxStamp ? "si" : "no",
                ),
            );
            yield put({
                type: types.SAVE_TAX_SUCCESS,
                taxRequest: response.data?.data,
            });
        } else {
            yield put({
                type: types.SAVE_TAX_FAILURE,
                taxRequest: response?.data?.message || response?.data?.data?.message,
            });
        }
    } catch (error) {
        yield put({
            type: types.SAVE_TAX_FAILURE,
            taxRequest: error?.response?.data?.message || error?.data?.message,
        });
    }
}

function* getStepData({ idStep }) {
    try {
        const response = yield call(environment.getStepData, { idStep });

        const stepDataBackup = JSON.parse(response.data.data?.lastHistoric?.extendedData || "null");
        if (idStep === "stepGeneralData") {
            yield put({
                type: types.SET_GENERAL_DATA_BACKUP,
                generalDataBackup: stepDataBackup,
            });
        }
        if (idStep === "stepTaxData") {
            yield put({
                type: types.SET_TAX_DATA_BACKUP,
                taxDataBackup: stepDataBackup,
            });
        }
        if (idStep === "stepLegalRepresentative") {
            yield put({
                type: types.SET_LEGAL_REPRESENTATIVE_BACKUP,
                legalRepresentativeBackup: stepDataBackup,
            });
        }
        if (idStep === "stepShareholders") {
            yield put({
                type: types.SET_SHAREHOLDERS_BACKUP,
                shareholdersBackup: stepDataBackup,
            });
        }
        if (idStep === "stepProxies") {
            yield put({
                type: types.SET_PROXIES_BACKUP,
                proxiesBackup: stepDataBackup,
            });
        }
        if (idStep === "stepBeneficiaries") {
            yield put({
                type: types.SET_BENEFICIARES_BACKUP,
                beneficiariesBackup: stepDataBackup,
            });
        }
        if (idStep === "stepDocumentation") {
            yield put({
                type: types.SET_DOCUMENTATION_BACKUP,
                documentationBackup: stepDataBackup,
            });
        }
        if (idStep === "stepSelectPaywayTerminalType") {
            yield put({
                type: types.SET_PAYWAY_TERMINAL_TYPE_BACKUP,
                paywayTerminalTypeBackup: stepDataBackup,
            });
        }
        if (idStep === "stepSelectPaywayCategory") {
            yield put({
                type: types.SET_PAYWAY_CATEGORY_BACKUP,
                paywayCategoryBackup: stepDataBackup,
            });
        }
        if (idStep === "stepSavePaywayTerminalsData") {
            yield put({
                type: types.SET_PAYWAY_TERMINALS_DATA_BACKUP,
                paywayTerminalsDataBackup: stepDataBackup,
            });
        }
    } catch (error) {
        yield put({
            type: types.SET_LOADING,
            loading: false,
        });
        console.log("file: environment.js:611 ~ function*getStepData ~ error:", error);
    }
}

function* deleteShareholder({ document, role, setState, notificationScope }) {
    try {
        const response = yield call(environment.deleteShareholder, { document, role });

        if (response && response.data?.code === "COR000I") {
            yield put({
                type: types.DELETE_SHAREHOLDERS_SUCCESS,
            });

            setState((prev) =>
                prev.filter((prevShareHolder) => prevShareHolder && prevShareHolder.documentNumber !== document),
            );

            yield put(notificationActions.showNotification(response.data.message, "success", [notificationScope]));
        } else {
            yield put({
                type: types.DELETE_SHAREHOLDERS_FAILURE,
            });
            yield put(notificationActions.showNotification(response.data.message, "error", [notificationScope]));
        }
    } catch (error) {
        yield put({
            type: types.DELETE_SHAREHOLDERS_FAILURE,
        });
        yield put(
            notificationActions.showNotification(error?.response?.data?.message || error?.data?.message, "error", [
                notificationScope,
            ]),
        );
    }
}

function* verifyPersonRoles({ cuil, role, isSigner }) {
    try {
        const response = yield call(environment.verifyPersonRoles, { cuil, role, isSigner });

        if (response && response.data?.code === "COR000I") {
            yield put({
                type: types.VERIFY_PERSON_ROLES_SUCCESS,
                verifyPersonRolesRequestValue: response.data?.data,
                documentNumber: cuil,
            });
        } else {
            yield put({
                type: types.VERIFY_PERSON_ROLES_FAILURE,
                verifyPersonRolesRequestValue: response?.data?.data?.role,
                documentNumber: cuil,
            });
        }
    } catch (error) {
        yield put({
            type: types.VERIFY_PERSON_ROLES_FAILURE,
            verifyPersonRolesRequestValue: error?.response?.data?.message || error?.data?.message,
            documentNumber: cuil,
        });
    }
}

function* saveAcceptPayway({ acceptPayway }) {
    try {
        const response = yield call(environment.acceptPaywayRequest, { acceptPayway });

        if (response && response.data?.code === "COR000I") {
            yield put({
                type: types.ACCEPT_PAYWAY_SUCCESS,
            });
            if (acceptPayway) {
                yield put(replace("/environment/stepSelectPaywayTerminalType"));
                yield put(environmentActions.getStepData("stepSelectPaywayTerminalType"));
            } else {
                yield put(replace("/environment/stepFinish"));
            }
        } else {
            yield put({
                type: types.ACCEPT_PAYWAY_FAILURE,
            });
        }
    } catch (error) {
        yield put({
            type: types.ACCEPT_PAYWAY_FAILURE,
        });
    }
}

function* savePaywayTerminalType({ terminalTypes }) {
    try {
        const response = yield call(environment.savePaywayTerminalTypeRequest, { terminalTypes });

        if (response && response.data?.code === "COR000I") {
            yield put({
                type: types.SELECT_PAYWAY_TERMINAL_TYPE_SUCCESS,
            });
            yield put(replace("/environment/stepSelectPaywayCategory"));
            yield put(environmentActions.getStepData("stepSelectPaywayCategory"));
        } else {
            yield put({
                type: types.SELECT_PAYWAY_TERMINAL_TYPE_FAILURE,
            });
        }
    } catch (error) {
        yield put({
            type: types.SELECT_PAYWAY_TERMINAL_TYPE_FAILURE,
        });
    }
}

function* getIsPaywayAccepted() {
    try {
        const response = yield call(environment.getIsPaywayAcceptedRequest);

        if (response && response.data?.code === "COR000I") {
            yield put({
                type: types.GET_IS_PAYWAY_ACCEPTED_SUCCESS,
                isPaywayAccepted: response.data?.data?.isPaywayAccepted,
            });
        } else {
            yield put({
                type: types.GET_IS_PAYWAY_ACCEPTED_FAILURE,
            });
            yield put(notificationActions.showNotification("returnCode.CLI002E", "error", ["environment"]));
        }
    } catch (error) {
        yield put({
            type: types.GET_IS_PAYWAY_ACCEPTED_FAILURE,
        });
        yield put(notificationActions.showNotification("returnCode.CLI002E", "error", ["environment"]));
    }
}

function* getPaywayCategories() {
    try {
        const response = yield call(environment.getPaywayCategoriesRequest);

        if (response && response.data?.code === "COR000I") {
            yield put({
                type: types.GET_PAYWAY_CATEGORIES_SUCCESS,
                paywayCategories: response.data?.data?.paywayCategories,
            });
        } else {
            yield put({
                type: types.GET_PAYWAY_CATEGORIES_FAILURE,
            });
            yield put(notificationActions.showNotification("returnCode.CLI002E", "error", ["environment"]));
        }
    } catch (error) {
        yield put({
            type: types.GET_PAYWAY_CATEGORIES_FAILURE,
        });
        yield put(notificationActions.showNotification("returnCode.CLI002E", "error", ["environment"]));
    }
}

function* verifyTyCAcceptance() {
    try {
        const response = yield call(environment.getAreTyCAccepted);

        if (response && response.data?.code === "COR000I") {
            yield put({
                type: types.VERIFY_ACCEPTED_TYC_SUCCESS,
                hasAcceptedPJTerms: response.data?.data?.hasAcceptedPJTerms,
            });
        } else {
            yield put({
                type: types.VERIFY_ACCEPTED_TYC_FAILURE,
            });
            yield put(notificationActions.showNotification("returnCode.CLI002E", "error", ["environment"]));
        }
    } catch (error) {
        yield put({
            type: types.VERIFY_ACCEPTED_TYC_FAILURE,
        });
        yield put(notificationActions.showNotification("returnCode.CLI002E", "error", ["environment"]));
    }
}

function* savePaywayCategory({ params }) {
    try {
        const response = yield call(environment.savePaywayCategoryRequest, params);

        if (response && response.data?.code === "COR000I") {
            yield put({
                type: types.SAVE_PAYWAY_CATEGORY_SUCCESS,
            });
            yield put(replace("/environment/stepSavePaywayTerminalsData"));
            yield put(environmentActions.getStepData("stepSavePaywayTerminalsData"));
        } else {
            yield put({
                type: types.SAVE_PAYWAY_CATEGORY_FAILURE,
            });
            yield put(
                notificationActions.showNotification(i18n.get("global.unexpectedError"), "error", ["environment"]),
            );
        }
    } catch (error) {
        yield put({
            type: types.SAVE_PAYWAY_CATEGORY_FAILURE,
        });
        yield put(notificationActions.showNotification(i18n.get("global.unexpectedError"), "error", ["environment"]));
    }
}

function* savePaywayTerminalsData({ paywayTerminalsData }) {
    try {
        const response = yield call(environment.savePaywayTerminalsDataRequest, { paywayTerminalsData });

        if (response && response.data?.code === "COR000I") {
            yield put({
                type: types.SAVE_PAYWAY_TERMINALS_DATA_SUCCESS,
            });
            yield put(replace("/environment/lastStepPayway"));
        } else {
            yield put({
                type: types.SAVE_PAYWAY_TERMINALS_DATA_FAILURE,
            });
            yield put(
                notificationActions.showNotification(i18n.get("global.unexpectedError"), "error", ["environment"]),
            );
        }
    } catch (error) {
        yield put({
            type: types.SAVE_PAYWAY_TERMINALS_DATA_FAILURE,
        });
        yield put(notificationActions.showNotification(i18n.get("global.unexpectedError"), "error", ["environment"]));
    }
}

function* lastStepPaywayPJ() {
    try {
        const response = yield call(environment.lastStepPayway);

        const { type } = response;
        if (type === "W") {
            yield put(
                notificationActions.showNotification(i18n.get("global.unexpectedError"), "error", ["environment"]),
            );
            yield put({ type: types.PAYWAY_LAST_STEP_FAILURE });
        } else {
            yield put({ type: types.PAYWAY_LAST_STEP_SUCCESS });

            yield put(replace("/"));
        }
    } catch (e) {
        yield put(notificationActions.showNotification(i18n.get("global.unexpectedError"), "error", ["environment"]));
        yield put({ type: types.PAYWAY_LAST_STEP_FAILURE });
    }
}
